(() => {
  if (customElements.get('mp-site-header')) return;

  class MpSiteHeader extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('[data-mp-menu-dialog]');
      this.toggle = this.querySelector('[data-mp-menu-toggle]');
      this.bar = this.querySelector('[data-mp-header-bar]');
      if (!this.dialog || !this.toggle || !this.bar) return;
      this.toggle.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-mp-menu-close]')?.addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
            this.dialog.close();
          }
          return;
        }
        const link = event.target.closest('a[href]');
        if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) this.dialog.close();
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        if (!(event.target instanceof Element)) return;
        const card = event.target.closest('[data-mp-menu-card]');
        if (!card || !this.contains(card)) return;
        this.open();
        card.scrollIntoView({ block: 'nearest' });
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.bar);
      const group = this.closest('#header-group');
      if (group) this.resizeObserver.observe(group);
      window.addEventListener('resize', () => {
        this.measure();
        this.queueNavUpdate();
      }, options);
      document.addEventListener('scroll', () => this.queueNavUpdate(), { ...options, capture: true, passive: true });
      const restoreHash = () => {
        cancelAnimationFrame(this.hashFrame);
        this.hashFrame = requestAnimationFrame(() => {
          this.hashFrame = null;
          // Horizon restores saved scroll positions on popstate/pageshow itself.
          if (!Number.isFinite(history.state?.scrollTop)) this.scrollToHash(false);
          this.queueNavUpdate();
        });
      };
      window.addEventListener('hashchange', restoreHash, options);
      window.addEventListener('popstate', restoreHash, options);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((name) => {
        document.addEventListener(name, () => this.queueNavBuild(), options);
      });
      this.contentObserver = new MutationObserver(() => this.queueNavBuild());
      const content = document.querySelector('#MainContent');
      if (content) this.contentObserver.observe(content, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['href', 'id', 'hidden']
      });
      this.buildPageNav();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.buildPageNav();
          restoreHash();
        }, { ...options, once: true });
      } else restoreHash();
      this.measure();
      this.dataset.ready = 'true';
    }

    measure() {
      if (!this.bar) return;
      const height = Math.ceil(this.bar.getBoundingClientRect().height);
      const navHeight = this.pageNav ? Math.ceil(this.pageNav.getBoundingClientRect().height) : 0;
      this.stackHeight = height + navHeight;
      const group = this.closest('#header-group');
      const groupHeight = group ? Math.ceil(group.getBoundingClientRect().height) : this.stackHeight;
      document.body.style.setProperty('--header-height', `${height}px`);
      document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
      document.documentElement.style.setProperty('--mp-header-height', `${height}px`);
      document.documentElement.style.setProperty('--mp-page-nav-height', `${navHeight}px`);
      document.documentElement.style.setProperty('--mp-header-stack-height', `${this.stackHeight}px`);
      this.queueNavUpdate();
    }

    queueNavBuild() {
      if (this.buildFrame) return;
      this.buildFrame = requestAnimationFrame(() => {
        this.buildFrame = null;
        if (this.isConnected) this.buildPageNav();
      });
    }

    targetForLink(link) {
      try {
        const url = new URL(link.getAttribute('href'), location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash) return null;
        return document.getElementById(decodeURIComponent(url.hash.slice(1)));
      } catch { return null; }
    }

    buildPageNav() {
      const sources = [...document.querySelectorAll('.mp-catalog__nav, [data-mp-page-nav]')].filter((source) => !this.contains(source));
      let source = null;
      let entries = [];
      for (const candidate of sources) {
        const seen = new Set();
        const links = [...candidate.querySelectorAll('a[href]')].flatMap((link) => {
          const target = this.targetForLink(link);
          const label = link.textContent.trim();
          if (!target || !label || seen.has(target) || target.closest('[hidden]') || !target.getClientRects().length) return [];
          seen.add(target);
          return [{ target, label, href: link.getAttribute('href') }];
        });
        if (links.length) {
          source = candidate;
          entries = links;
          break;
        }
      }
      const unchanged = source === this.navSource && entries.length === (this.navEntries?.length || 0) && entries.every((entry, index) => {
        const previous = this.navEntries[index];
        return entry.target === previous.target && entry.label === previous.label && entry.href === previous.href;
      });
      if (unchanged) return;
      this.clearPageNav();
      if (source) {
        this.navSource = source;
        this.navEntries = entries;
        this.pageNav = document.createElement('nav');
        this.pageNav.className = 'mp-site-header__page-nav';
        this.pageNav.setAttribute('aria-label', source.getAttribute('aria-label') || 'On this page');
        const inner = document.createElement('div');
        inner.className = 'mp-site-header__page-nav-inner';
        entries.forEach((entry) => {
          entry.link = document.createElement('a');
          entry.link.setAttribute('href', entry.href);
          entry.link.textContent = entry.label;
          inner.append(entry.link);
        });
        this.pageNav.append(inner);
        this.bar.after(this.pageNav);
        this.navController = new AbortController();
        this.pageNav.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const entry = this.navEntries.find((item) => item.link === link);
          if (!entry) return;
          event.preventDefault();
          const url = new URL(entry.href, location.href);
          if (url.hash !== location.hash) {
            const state = history.state && typeof history.state === 'object' ? { ...history.state } : {};
            // A fresh anchor must not inherit the previous entry's saved position.
            delete state.scrollTop;
            history.pushState(state, '', url.hash);
          }
          this.scrollToTarget(entry.target, true);
        }, { signal: this.navController.signal });
        source.dataset.mpNavSource = 'true';
        this.resizeObserver.observe(this.pageNav);
      }
      this.measure();
    }

    clearPageNav() {
      this.navController?.abort();
      this.navController = null;
      if (this.pageNav) this.resizeObserver?.unobserve(this.pageNav);
      this.pageNav?.remove();
      this.navSource?.removeAttribute('data-mp-nav-source');
      this.pageNav = null;
      this.navSource = null;
      this.navEntries = [];
    }

    scrollOwner(target) {
      for (let parent = target.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        if (/(auto|scroll|overlay)/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight + 1) return parent;
      }
      return window;
    }

    scrollToTarget(target, smooth) {
      const owner = this.scrollOwner(target);
      const top = owner === window ? 0 : owner.getBoundingClientRect().top + owner.clientTop;
      const scrollTop = owner === window ? window.scrollY : owner.scrollTop;
      const behavior = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant';
      owner.scrollTo({ top: Math.max(0, scrollTop + target.getBoundingClientRect().top - top - this.stackHeight - 16), behavior });
      this.queueNavUpdate();
    }

    scrollToHash(smooth) {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      const entry = this.navEntries?.find((item) => item.target.id === id);
      if (entry) this.scrollToTarget(entry.target, smooth);
    }

    queueNavUpdate() {
      if (this.navFrame) return;
      this.navFrame = requestAnimationFrame(() => {
        this.navFrame = null;
        if (!this.isConnected || !this.navEntries?.length) return;
        const threshold = this.pageNav.getBoundingClientRect().bottom + 24;
        const entries = this.navEntries.filter((entry) => entry.target.isConnected && entry.target.getClientRects().length)
          .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
        const last = entries.at(-1);
        const owner = last ? this.scrollOwner(last.target) : window;
        const scrollTop = owner === window ? window.scrollY : owner.scrollTop;
        const viewport = owner === window ? window.innerHeight : owner.clientHeight;
        const extent = owner === window ? document.scrollingElement.scrollHeight : owner.scrollHeight;
        const atBottom = extent > viewport + 1 && scrollTop + viewport >= extent - 2;
        // Short final sections may never reach the header before scrolling clamps.
        const current = atBottom ? last : entries.filter((entry) => entry.target.getBoundingClientRect().top <= threshold).at(-1) || entries[0];
        this.navEntries.forEach((entry) => {
          if (entry === current) entry.link.setAttribute('aria-current', 'location');
          else entry.link.removeAttribute('aria-current');
        });
      });
    }

    lockScroll() {
      if (this.savedScrollStyles) return;
      const targets = [...new Set([document.body, document.querySelector('.page-wrapper')].filter(Boolean))];
      this.savedScrollStyles = targets.map((element) => ({
        element,
        properties: ['overflow', 'overflow-x', 'overflow-y'].map((property) => ({
          property,
          value: element.style.getPropertyValue(property),
          priority: element.style.getPropertyPriority(property)
        }))
      }));
      targets.forEach((element) => element.style.setProperty('overflow', 'hidden', 'important'));
    }

    unlockScroll() {
      if (!this.savedScrollStyles) return;
      this.savedScrollStyles.forEach(({ element, properties }) => {
        properties.forEach(({ property }) => element.style.removeProperty(property));
        properties.forEach(({ property, value, priority }) => {
          if (value) element.style.setProperty(property, value, priority);
        });
      });
      this.savedScrollStyles = null;
    }

    open() {
      if (!this.dialog || this.dialog.open) return;
      this.returnFocus = document.activeElement;
      this.dialog.showModal();
      this.lockScroll();
      this.toggle.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-mp-menu-close]')?.focus({ preventScroll: true });
    }

    afterClose() {
      this.unlockScroll();
      this.toggle?.setAttribute('aria-expanded', 'false');
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.unlockScroll();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.contentObserver?.disconnect();
      [this.buildFrame, this.navFrame, this.hashFrame].forEach((frame) => cancelAnimationFrame(frame));
      this.buildFrame = this.navFrame = this.hashFrame = null;
      this.clearPageNav();
      document.documentElement.style.setProperty('--mp-page-nav-height', '0px');
      document.documentElement.style.setProperty('--mp-header-stack-height', `${this.bar?.getBoundingClientRect().height || 76}px`);
      this.controller = null;
      this.returnFocus = null;
    }
  }

  customElements.define('mp-site-header', MpSiteHeader);
})();
